import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// KPI definitions: how to colour each cell.
// For "risk" metrics (speeding, idling, NG) higher = worse → red scale.
// For "efficiency" metric (kmpl) higher = better → green scale.
const KPIS = [
    { key: "speeding_rate",       label: "速度超過率",    unit: "%",    risk: true,  thresholds: [5, 15, 30] },
    { key: "idling_rate",         label: "アイドル率",    unit: "%",    risk: true,  thresholds: [5, 15, 30] },
    { key: "inspection_ng_rate",  label: "点検NG率",     unit: "%",    risk: true,  thresholds: [5, 15, 30] },
    { key: "fuel_efficiency_kmpl", label: "平均燃費",     unit: "km/L", risk: false, thresholds: [8, 12, 16] },
];

function cellColor(value, kpi) {
    if (value == null) return "bg-transparent text-[var(--prc-muted)]";
    const [t1, t2, t3] = kpi.thresholds;
    if (kpi.risk) {
        if (value >= t3) return "bg-rose-500/30 text-rose-100 border-rose-500/50";
        if (value >= t2) return "bg-rose-500/15 text-rose-200 border-rose-500/30";
        if (value >= t1) return "bg-amber-500/15 text-amber-200 border-amber-500/30";
        return "bg-[var(--prc-green)]/10 text-[var(--prc-green)] border-[var(--prc-green)]/30";
    }
    if (value >= t3) return "bg-[var(--prc-green)]/25 text-[var(--prc-green)] border-[var(--prc-green)]/40";
    if (value >= t2) return "bg-[var(--prc-green)]/10 text-[var(--prc-green)] border-[var(--prc-green)]/30";
    if (value >= t1) return "bg-amber-500/10 text-amber-200 border-amber-500/30";
    return "bg-rose-500/15 text-rose-200 border-rose-500/30";
}

export default function DepartmentHeatmap() {
    const [data, setData] = useState(null);
    const [months, setMonths] = useState(6);
    const [activeKpi, setActiveKpi] = useState("speeding_rate");

    useEffect(() => {
        api.get(`/analytics/departments/heatmap?months=${months}`)
            .then(({ data }) => setData(data))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "読込失敗"));
    }, [months]);

    if (!data) return <p className="text-sm text-[var(--prc-muted)] font-mono uppercase tracking-wider animate-pulse" data-testid="dept-loading">// LOADING...</p>;

    const kpi = KPIS.find((k) => k.key === activeKpi);

    return (
        <div className="space-y-5" data-testid="dept-heatmap-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">// 部署別 KPI ヒートマップ</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight">支店・部署別 運用品質</h1>
                    <p className="text-sm text-[var(--prc-muted)] mt-1">
                        車両の <code className="text-xs">department</code> 属性で集計。色が濃いセルほど要注意（速度超過・アイドリング・NG は赤、燃費は逆）。
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="prc-label-overline text-xs">期間</label>
                    <select
                        className="prc-input !w-auto"
                        value={months}
                        onChange={(e) => setMonths(parseInt(e.target.value))}
                        data-testid="dept-months-select"
                    >
                        <option value="3">過去3か月</option>
                        <option value="6">過去6か月</option>
                        <option value="12">過去12か月</option>
                    </select>
                </div>
            </div>

            {/* Department × KPI Heatmap */}
            <div className="prc-card overflow-x-auto" data-testid="dept-heatmap-table">
                <table className="prc-table">
                    <thead>
                        <tr>
                            <th className="text-left">部署</th>
                            <th className="text-right">車両数</th>
                            <th className="text-right">運行回数</th>
                            <th className="text-right">走行km</th>
                            {KPIS.map((k) => (
                                <th key={k.key} className="text-right min-w-[96px]">
                                    {k.label}<br /><span className="text-[10px] text-[var(--prc-muted)] font-normal">{k.unit}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.departments.length === 0 && (
                            <tr><td colSpan={4 + KPIS.length} className="p-8 text-center text-sm text-[var(--prc-muted)]" data-testid="dept-empty">集計対象となる車両がありません</td></tr>
                        )}
                        {data.departments.map((d) => (
                            <tr key={d.name} data-testid={`dept-row-${d.name}`}>
                                <td className="font-semibold">{d.name}</td>
                                <td className="num">{d.vehicle_count}</td>
                                <td className="num">{d.rates.trips || 0}</td>
                                <td className="num">{(d.rates.total_km || 0).toLocaleString()}</td>
                                {KPIS.map((k) => {
                                    const v = d.rates[k.key];
                                    return (
                                        <td key={k.key} className="num">
                                            <span className={`inline-block px-2 py-0.5 rounded-sm border font-mono font-semibold ${cellColor(v, k)}`}>
                                                {v == null ? "—" : v}
                                            </span>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Monthly trend selector */}
            <div className="prc-card p-4" data-testid="dept-trend">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <p className="prc-label-overline mb-0">// 月次推移</p>
                    <div className="flex gap-1">
                        {KPIS.map((k) => (
                            <button
                                key={k.key}
                                type="button"
                                onClick={() => setActiveKpi(k.key)}
                                className={`px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-wider transition ${
                                    activeKpi === k.key
                                        ? "border-[var(--prc-green)] bg-[var(--prc-green)]/10 text-[var(--prc-green)]"
                                        : "border-[var(--prc-border)] text-[var(--prc-muted)] hover:text-white"
                                }`}
                                data-testid={`dept-trend-kpi-${k.key}`}
                            >{k.label}</button>
                        ))}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="prc-table">
                        <thead>
                            <tr>
                                <th className="text-left min-w-[140px]">部署</th>
                                {data.months.map((m) => (
                                    <th key={m} className="text-center min-w-[72px]">
                                        <span className="font-mono text-[10px] text-[var(--prc-muted)]">{m}</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.departments.map((d) => (
                                <tr key={d.name}>
                                    <td className="font-semibold">{d.name}</td>
                                    {d.monthly.map((m) => {
                                        const v = m[activeKpi];
                                        return (
                                            <td key={m.month} className="num p-0.5">
                                                <div className={`mx-auto w-full h-9 flex items-center justify-center rounded-sm border font-mono text-xs font-semibold ${cellColor(v, kpi)}`} title={`${m.month}: ${v == null ? "データなし" : v + kpi.unit}`}>
                                                    {v == null ? "—" : v}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-[10px] text-[var(--prc-muted)] mt-2 font-mono uppercase tracking-wider">
                    // 凡例: {kpi.risk
                        ? <>低 (緑 &lt;{kpi.thresholds[0]}%) → 警戒 (琥珀 {kpi.thresholds[0]}–{kpi.thresholds[1]}%) → 要改善 (赤 {kpi.thresholds[1]}–{kpi.thresholds[2]}%) → 深刻 (濃赤 ≥{kpi.thresholds[2]}%)</>
                        : <>低 (赤 &lt;{kpi.thresholds[0]}) → 警戒 (琥珀 {kpi.thresholds[0]}–{kpi.thresholds[1]}) → 良好 (緑 {kpi.thresholds[1]}–{kpi.thresholds[2]}) → 優秀 (濃緑 ≥{kpi.thresholds[2]})</>}
                </p>
            </div>
        </div>
    );
}
